// ============================================
// 🩺 B252 (AI_114) — DECLARAÇÃO DE AGENDAMENTO (serviço de enfermagem)
// ============================================
// Documento PRÓPRIO da enfermagem (não é prescrição nem laudo médico). A
// enfermeira é a autora. Layout COPIADO do Pedido de Exame (gerarPDF) — mesma
// estrutura e fonte moderna (Helvetica Neue), cabeçalho com logo + ambulatório +
// nome/registro do profissional logado (aqui a ENFERMEIRA + COREN), linha verde,
// e rodapé com nome/registro + endereço + cidade/data. Encerra SEM bloco de
// assinatura (a assinatura será digital, B212).

import { Paciente } from "../../../types";
import logoBase64 from "../../prescricoes/services/logoBase64";
import { abrirPDFEmNovaJanela } from "../../prescricoes/services/gerarPDF";

export interface DadosDeclaracaoAgendamento {
  agendamento: string; // procedimento/exame agendado (ex.: "Biópsia Hepática guiada por USG")
  data: string; // data do agendamento, dd/mm/aaaa
  hora?: string; // hora (ex.: "08h00")
  setor: string; // setor (ex.: "Radiologia")
  hospital?: string; // hospital/unidade (ex.: "Hospital de Base Dr. Ary Pinheiro"); pode ser fora do HB
  medicoExecutante?: string; // opcional
  observacoes?: string; // texto livre (pode conter várias linhas)
}

// Espelha o ConfigPDF do Pedido de Exame, mas o "profissional" é a ENFERMEIRA.
export interface ConfigDeclaracao {
  nomeAmbulatorio: string;
  nomeProfissional: string; // nome da enfermeira logada
  registro: string; // COREN (ex.: "COREN 490697 RO"); pode ser ""
  endereco: string;
  cidade: string;
}

// Atalho usado no modal: hospital padrão do serviço (programações no HB).
export const HOSPITAL_PADRAO = "Hospital de Base Dr. Ary Pinheiro";

// Dados institucionais fixos (iguais ao Pedido de Exame). O que varia é o
// PROFISSIONAL (a enfermeira autora + COREN).
export const AMBULATORIO_DECLARACAO = {
  nomeAmbulatorio: "Ambulatório de Transplante Hepático de Rondônia",
  endereco: "Av. Gov. Jorge Teixeira, 3766",
  cidade: "Porto Velho - RO",
};

export function montarConfigDeclaracao(
  nomeProfissional: string,
  registro: string
): ConfigDeclaracao {
  return { ...AMBULATORIO_DECLARACAO, nomeProfissional, registro };
}

// Escapa texto do usuário/paciente para não quebrar o HTML do documento.
function esc(v: string | undefined | null): string {
  if (v === undefined || v === null) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Monta o HTML completo do documento (função PURA — testável sem DOM).
export function montarHTMLDeclaracaoAgendamento(
  paciente: Paciente,
  dados: DadosDeclaracaoAgendamento,
  config: ConfigDeclaracao
): string {
  const dataEmissao = new Date().toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const cns = (paciente.cns || "").trim();
  const hospub = (paciente.hospub || "").trim();
  const dataAg = esc(dados.data) + (dados.hora ? ` às ${esc(dados.hora)}` : "");
  const hospital = (dados.hospital || "").trim();

  // Corpo da declaração (com os ajustes de Leonardo: "Tem programação de" no
  // lugar de "Possui"; após o setor, "do {hospital}").
  const corpo =
    `Declaro para os devidos fins que a Sr(a) <strong>${esc(paciente.nome)}</strong>` +
    (cns ? ` CNS: ${esc(cns)}` : "") +
    (hospub ? ` Hospub: ${esc(hospub)}` : "") +
    `. Tem programação de <strong>${esc(dados.agendamento)}</strong>, está ` +
    `programado(a) para o dia <strong>${dataAg}</strong> no ${esc(dados.setor)}` +
    (hospital ? ` do ${esc(hospital)}` : "") +
    `.` +
    (dados.medicoExecutante
      ? `<br/>Médico executante: ${esc(dados.medicoExecutante)}.`
      : "");

  // Observações: preserva quebras de linha do que a enfermeira digitou.
  const observacoesHTML = (dados.observacoes || "").trim()
    ? `<div class="observacao">
         <p class="observacao-titulo">*OBSERVAÇÃO:</p>
         <div class="observacao-corpo">${esc(dados.observacoes).replace(
           /\n/g,
           "<br/>"
         )}</div>
       </div>`
    : "";

  return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Declaração de Agendamento - ${esc(paciente.nome)}</title>
  <style>
    @page { size: A4; margin: 20mm 15mm 25mm 15mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
      color: #1a1a1a;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .page { max-width: 210mm; margin: 0 auto; padding: 0; }
    .header {
      display: flex; align-items: center; gap: 16px;
      padding-bottom: 12px; border-bottom: 3px solid #2d6a2e; margin-bottom: 24px;
    }
    .header-logo { width: 80px; height: 80px; flex-shrink: 0; }
    .header-logo img { width: 100%; height: 100%; object-fit: contain; }
    .header-text { flex: 1; }
    .header-text h1 {
      font-size: 18px; font-weight: 700; color: #2d6a2e;
      text-transform: uppercase; margin-bottom: 4px;
    }
    .header-text p { font-size: 13px; color: #444; font-style: italic; }
    .tipo-documento {
      font-size: 15px; font-weight: 700; font-style: italic; margin-bottom: 16px;
    }
    .corpo { font-size: 14px; line-height: 1.9; text-align: justify; }
    .observacao { margin-top: 18px; font-size: 14px; line-height: 1.8; }
    .observacao-titulo { font-weight: 700; }
    .rodape {
      margin-top: 40px; padding-top: 16px; border-top: 2px solid #2d6a2e;
      text-align: center;
    }
    .rodape-crm { font-size: 13px; font-weight: 600; color: #333; margin-bottom: 4px; }
    .rodape-endereco { font-size: 11px; color: #888; }
    .rodape-data {
      font-size: 13px; color: #333; margin-top: 12px; text-align: right;
    }
    @media print { body { margin: 0; } .no-print { display: none !important; } }
  </style>
</head>
<body>
  <div class="no-print" style="text-align: center; padding: 12px; background: #f0f0f0;">
    <button onclick="window.print()" style="padding: 10px 32px; background: #2563eb; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; margin-right: 8px;">
      🖨️ Imprimir
    </button>
    <button onclick="window.close()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>
  </div>

  <div class="page">
    <!-- CABEÇALHO (igual ao Pedido de Exame) -->
    <div class="header">
      <div class="header-logo"><img src="${logoBase64}" alt="Logo" /></div>
      <div class="header-text">
        <h1>${esc(config.nomeAmbulatorio)}</h1>
        <p>${esc(config.nomeProfissional)}${
    config.registro ? ` — ${esc(config.registro)}` : ""
  }</p>
      </div>
    </div>

    <!-- TÍTULO DO DOCUMENTO -->
    <div class="tipo-documento">Declaração de Agendamento</div>

    <!-- CORPO -->
    <div class="corpo">${corpo}</div>

    ${observacoesHTML}

    <!-- RODAPÉ (igual ao Pedido de Exame) -->
    <div class="rodape">
      <div class="rodape-crm">${esc(config.nomeProfissional)}${
    config.registro ? ` — ${esc(config.registro)}` : ""
  }</div>
      ${config.endereco ? `<div class="rodape-endereco">${esc(config.endereco)}</div>` : ""}
      <div class="rodape-data">${esc(config.cidade)}, ${dataEmissao}.</div>
    </div>
  </div>
</body>
</html>
  `;
}

// Gera o documento e abre em nova janela para impressão.
export function gerarPDFDeclaracaoAgendamento(
  paciente: Paciente,
  dados: DadosDeclaracaoAgendamento,
  config: ConfigDeclaracao
): void {
  const html = montarHTMLDeclaracaoAgendamento(paciente, dados, config);
  abrirPDFEmNovaJanela(html);
}
